import React from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

/**
 * Reusable donut chart (Recharts) used across the admin dashboard.
 *
 * Props:
 *  - data: [{ name, value }]
 *  - colors: array of hex colors (one per slice)
 *  - size: pixel diameter of the chart (default 170)
 *  - thickness: ring thickness as a fraction of the radius (default 0.34)
 *  - centerValue / centerLabel: optional text shown in the donut hole
 *  - valueSuffix: appended to the value in the tooltip (e.g. "%")
 */

const DonutTooltip = ({ active, payload, valueSuffix }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--tp-border)",
        borderRadius: 8,
        boxShadow: "var(--tp-shadow-elevated)",
        padding: "6px 10px",
        fontSize: 12,
        color: "var(--tp-text)",
        display: "flex",
        alignItems: "center",
        gap: 8,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: item.payload?.fill || item.color,
          flexShrink: 0,
        }}
      />
      <span style={{ fontWeight: 500 }}>{item.name}</span>
      <span style={{ marginLeft: 4, fontWeight: 600 }}>
        {item.value}
        {valueSuffix}
      </span>
    </div>
  );
};

const DonutChart = ({
  data = [],
  colors = [],
  size = 170,
  thickness = 0.34,
  centerValue,
  centerLabel,
  centerValueSize,
  valueSuffix = "",
}) => {
  const numeric = data.map((d) => ({
    name: d.name,
    value: Number(d.value) || 0,
  }));
  const allZero = numeric.every((d) => d.value === 0);
  const chartData = allZero ? [{ name: "No data", value: 1 }] : numeric;
  const chartColors = allZero ? ["#eef1f5"] : colors;

  const outerRadius = size / 2 - 2;
  const innerRadius = outerRadius * (1 - thickness);
  const valueFontSize = centerValueSize || Math.round(size * 0.16);

  return (
    <div
      style={{ position: "relative", width: size, height: size, flexShrink: 0 }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={90}
            endAngle={-270}
            paddingAngle={chartData.length > 1 ? 2 : 0}
            stroke="none"
            cornerRadius={chartData.length > 1 ? 3 : 0}
          >
            {chartData.map((entry, i) => (
              <Cell key={i} fill={chartColors[i % chartColors.length]} />
            ))}
          </Pie>
          {!allZero && (
            <Tooltip
              content={<DonutTooltip valueSuffix={valueSuffix} />}
              wrapperStyle={{ outline: "none", zIndex: 10 }}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
      {centerValue !== undefined && centerValue !== null && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <span
            style={{
              fontSize: valueFontSize,
              fontWeight: 600,
              color: "var(--tp-text)",
              lineHeight: 1.1,
            }}
          >
            {centerValue}
          </span>
          {centerLabel && (
            <span
              style={{
                fontSize: valueFontSize * 0.5,
                fontWeight: 400,
                color: "var(--tp-text-muted)",
              }}
            >
              {centerLabel}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
